/**
 * The amount field's editing model — pure, and deliberately separate from any
 * component.
 *
 * WHY THIS IS A STATE MACHINE AND NOT AN <input>'s OWN BEHAVIOUR.
 * Every keystroke arrives from our own keypad, not the OS keyboard, so the
 * field is a DISPLAY SURFACE and this module owns the truth: which digits
 * exist, where the caret is, whether the whole amount is selected, and what was
 * last cleared. That inversion is what makes separators safe — the display can
 * be reformatted on every press without ever fighting a browser caret, because
 * the caret is a digit index here and only projected into the DOM afterwards.
 *
 * THE TWO RULES IT ENCODES, both from "never ask the user to be precise":
 *   - Tapping the field selects the whole amount, so the next press replaces it
 *     and one backspace clears it. Aiming a caret between tabular digits on a
 *     touchscreen is a bad ask, and worse in RTL.
 *   - That clear is undoable: a second backspace with nothing typed in between
 *     puts the number back. Discoverable by accident — someone who clears by
 *     mistake presses the same key again on instinct and finds their number.
 */

/** Matches the persisted-amount contract in src/session/schema.ts. */
const MAX_INTEGER_DIGITS = 15;
const MAX_FRACTION_DIGITS = 8;

export interface FieldState {
  /** Digits and at most one dot. Never contains separators. */
  readonly raw: string;
  /** True when the whole amount is selected, so the next press replaces it. */
  readonly selectAll: boolean;
  /** Caret position as an index into `raw`, used only when selectAll is false. */
  readonly caret: number;
  /** The value a clear removed, restorable until something else is typed. */
  readonly cleared: string | null;
}

export function fieldFrom(raw: string): FieldState {
  return { raw, selectAll: false, caret: raw.length, cleared: null };
}

/** Tapping the field: select everything. */
export function focusField(state: FieldState): FieldState {
  return { ...state, selectAll: true, caret: state.raw.length };
}

/**
 * A second tap inside an already-selected field drops into caret editing, for
 * the rare person who wants to change one digit. An escape hatch nobody else
 * pays for.
 */
export function enterCaretMode(state: FieldState, caret?: number): FieldState {
  const at = caret ?? state.raw.length;
  return { ...state, selectAll: false, caret: clampCaret(state.raw, at) };
}

export function setCaret(state: FieldState, caret: number): FieldState {
  return { ...state, selectAll: false, caret: clampCaret(state.raw, caret) };
}

function clampCaret(raw: string, caret: number): number {
  return Math.max(0, Math.min(raw.length, caret));
}

function splitRaw(raw: string): { int: string; frac: string | null } {
  const dot = raw.indexOf(".");
  if (dot === -1) return { int: raw, frac: null };
  return { int: raw.slice(0, dot), frac: raw.slice(dot + 1) };
}

/** Rejects an edit that would exceed the persisted-amount limits. */
function withinLimits(raw: string): boolean {
  const { int, frac } = splitRaw(raw);
  if (int.length > MAX_INTEGER_DIGITS) return false;
  if (frac !== null && frac.length > MAX_FRACTION_DIGITS) return false;
  return true;
}

function insert(raw: string, caret: number, text: string): string {
  return raw.slice(0, caret) + text + raw.slice(caret);
}

export function pressDigit(state: FieldState, digit: string): FieldState {
  if (!/^[0-9]$/.test(digit)) return state;

  if (state.selectAll) {
    // Replaces the whole amount. This is the common case by a wide margin.
    return { raw: digit, selectAll: false, caret: 1, cleared: null };
  }

  const next = insert(state.raw, state.caret, digit);
  if (!withinLimits(next)) return state;
  return { raw: next, selectAll: false, caret: state.caret + 1, cleared: null };
}

export function pressDot(state: FieldState): FieldState {
  if (state.selectAll) {
    // "0." rather than "." — a leading zero is what people expect to see, and
    // it keeps the field from ever reading as an orphaned separator.
    return { raw: "0.", selectAll: false, caret: 2, cleared: null };
  }
  if (state.raw.includes(".")) return state;

  const base = state.raw.length === 0 ? "0" : state.raw;
  const caret = state.raw.length === 0 ? 1 : state.caret;
  const next = insert(base, caret, ".");
  if (!withinLimits(next)) return state;
  return { raw: next, selectAll: false, caret: caret + 1, cleared: null };
}

/**
 * One press clears everything; the next press puts it back.
 *
 * In caret mode it behaves as an ordinary backspace, deleting one character,
 * because someone who explicitly entered caret editing is asking for precision
 * and should get it.
 */
export function pressBackspace(state: FieldState): FieldState {
  // Restore: nothing has been typed since the clear.
  if (state.raw === "" && state.cleared !== null) {
    return { raw: state.cleared, selectAll: true, caret: state.cleared.length, cleared: null };
  }

  if (state.selectAll) {
    if (state.raw === "") return state;
    return { raw: "", selectAll: false, caret: 0, cleared: state.raw };
  }

  if (state.caret === 0) return state;
  const next = state.raw.slice(0, state.caret - 1) + state.raw.slice(state.caret);
  return { raw: next, selectAll: false, caret: state.caret - 1, cleared: null };
}

/** AC. Also restorable, on the same second-press rule as backspace. */
export function pressClear(state: FieldState): FieldState {
  if (state.raw === "" && state.cleared !== null) {
    return { raw: state.cleared, selectAll: true, caret: state.cleared.length, cleared: null };
  }
  if (state.raw === "") return state;
  return { raw: "", selectAll: false, caret: 0, cleared: state.raw };
}

/* ------------------------------------------------------------------ *
 * Display projection
 * ------------------------------------------------------------------ */

/**
 * Groups the integer part with Latin commas. Separators are shown WHILE typing,
 * not only at rest: a large unseparated figure is hard to read at a glance, and
 * reading the number is the product. This is only safe because the caret is a
 * digit index in this module rather than a DOM offset — see caretToDisplay.
 */
export function displayValue(raw: string): string {
  if (raw === "") return "";
  const { int, frac } = splitRaw(raw);
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return frac === null ? grouped : `${grouped}.${frac}`;
}

/**
 * Projects a caret position in `raw` onto the separated display string.
 *
 * Necessary because "1250" and "1,250" have different lengths: a caret after
 * the third raw character sits after the fourth display character. Without this
 * mapping, every inserted separator would drag the caret one place left.
 */
export function caretToDisplay(raw: string, caret: number): number {
  const display = displayValue(raw);
  let rawSeen = 0;
  for (let i = 0; i < display.length; i += 1) {
    if (rawSeen === caret) return i;
    if (display[i] !== ",") rawSeen += 1;
  }
  return display.length;
}

/** The inverse: a click at a display offset becomes a raw caret index. */
export function caretFromDisplay(raw: string, displayCaret: number): number {
  const display = displayValue(raw);
  let rawIndex = 0;
  for (let i = 0; i < Math.min(displayCaret, display.length); i += 1) {
    if (display[i] !== ",") rawIndex += 1;
  }
  return rawIndex;
}
